import { createContext, useContext } from 'react'
import { InspectorLayersTreeNode } from '../sketchSceneLib'

export type InspectorContext = {
  isInspectorActive: boolean
  /** True when the inspector data coming from the JSON skeleton file is ready */
  inspectorDataIsReady: boolean
  /** True when inspectorDataIsReady is true and the renderer has also finished rendering.  */
  isInspectorReady: boolean
  layersTreeRootNode: InspectorLayersTreeNode | null
  selectedNode: InspectorLayersTreeNode | null
  /** True means something went wrong while loading the data */
  hasInspectorDataError: boolean
  /**
   * Allow to overwrite the selected layer node from a user interaction other than
   * clicking on the canvas.
   */
  setSelectedLayerNode: (node: InspectorLayersTreeNode | null) => void
  /**
   * True if at least one layer in the artboard has exportable assets. This does not
   * count exportable assets at the artboard level.
   * This is use to decide if we want to show the "No exportable assets" message
   * or if we prefer to not show anything when the selected layer does not have any
   * exportable assets.
   * @see https://github.com/sketch-hq/Cloud/issues/6669#issuecomment-748183677
   */
  hasAnyLayerWithExportableAssets: boolean
  /**
   * The handler function to attach to onClick prop of the WebRendererCanvas component
   * to handle layer selection.
   */
  clickHandler: (event: React.MouseEvent<HTMLElement>) => void
  /**
   * The handler function to attach to the onMouseMove prop of the WebRendererCanvas component
   * to handle the hovered layer.
   */
  mouseMoveHandler: (event: React.MouseEvent<HTMLElement>) => void
}

export const inspectorContext = createContext<InspectorContext | undefined>(
  undefined
)

export const inspectorHoveredNodeContext = createContext<
  InspectorLayersTreeNode | null | undefined
>(undefined)

export function useInspectorContext() {
  const value = useContext(inspectorContext)

  if (typeof value === 'undefined') {
    throw new Error(
      'useSelectedNode must be used within a ArtboardDetailInspectorProvider'
    )
  }

  return value
}

export function useInspectorHoveredNode() {
  const value = useContext(inspectorHoveredNodeContext)

  if (typeof value === 'undefined') {
    throw new Error(
      'useHoveredNode must be used within a ArtboardDetailInspectorProvider'
    )
  }

  return value
}
